// SMS 문자 채팅방 (고객 전화번호별 방, 양방향). PC 대시보드 전용.
// 데이터는 백엔드 /dashboard/sms/* (원격 appdb 읽기 + 발송 큐 INSERT, 로컬 연락처/메모)에서 가져온다.
// 수신/상태는 윈도우 워커가 appdb에 직접 기록 → 소켓 알림 불가 → 3초 폴링으로 갱신.
// UI: 메세지함(방 목록 → 클릭 시 모달 대화) / 연락처 관리하기 / 공유 메모(아이콘→모달).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from '../componentsStyle/sms.module.css';
import { getFetch, postFetch } from '../../fetch/fetch';
import ImageModal2 from './use/imagemodal2';
import SmsBulkSend from './smsbulksend';
import { Button, Chip, CircularProgress, Divider, Switch, TextField, TextareaAutosize, Tooltip } from '@mui/material';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import SendIcon from '@mui/icons-material/Send';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DoneIcon from '@mui/icons-material/Done';
import ErrorIcon from '@mui/icons-material/Error';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import SearchIcon from '@mui/icons-material/Search';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import InterviewScheduleModal from './interview/interviewschedulemodal';
import JobOpeningModal from './jobopening/jobopeningmodal';
import CampaignIcon from '@mui/icons-material/Campaign';
import { InterviewPrefill, parseRoomDefaults } from './interview/interviewconstants';

//전화번호 보기 좋게 (서버 전송/조회는 항상 raw)
const formatPhone = (p: string) => {
    if (!p) return "";
    const d = ("" + p).replace(/[^0-9]/g, "");
    if (d.length === 11) return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
    if (d.length === 10) {
        if (d.startsWith("02")) return `${d.slice(0, 2)}-${d.slice(2, 6)}-${d.slice(6)}`;
        return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
    }
    return p;
};

//"YYYY-MM-DD HH:mm:ss" → "오전/오후 h:mm"
const formatClock = (s: string) => {
    if (!s) return "";
    const time = (s.split(" ")[1]) || "";
    const [hh, mm] = time.split(":");
    if (hh === undefined || mm === undefined) return "";
    const h = +hh;
    const ampm = h < 12 ? "오전" : "오후";
    let h12 = h % 12;
    if (h12 === 0) h12 = 12;
    return `${ampm} ${h12}:${mm}`;
};

//방 목록 시각(카카오톡식): 오늘이면 시계, 어제면 "어제", 같은 해면 "M월 D일", 이전 해면 "YYYY. M. D."
const formatRoomTime = (s: string) => {
    if (!s) return "";
    const datePart = (s.split(" ")[0]) || "";
    const parts = datePart.split("-");
    if (parts.length < 3) return datePart;
    const y = +parts[0], m = +parts[1], d = +parts[2];
    const now = new Date();
    if (y === now.getFullYear() && m === now.getMonth() + 1 && d === now.getDate()) return formatClock(s);
    const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    if (y === yesterday.getFullYear() && m === yesterday.getMonth() + 1 && d === yesterday.getDate()) return "어제";
    if (y === now.getFullYear()) return `${m}월 ${d}일`;
    return `${y}. ${m}. ${d}.`;
};

//대화 모달 날짜 구분선: "YYYY-MM-DD HH:mm:ss" → "2026년 7월 9일 목요일"
const dayNames = ["일", "월", "화", "수", "목", "금", "토"];
const formatDateDivider = (s: string) => {
    if (!s) return "";
    const parts = ((s.split(" ")[0]) || "").split("-");
    if (parts.length < 3) return "";
    const y = +parts[0], m = +parts[1], d = +parts[2];
    return `${y}년 ${m}월 ${d}일 ${dayNames[new Date(y, m - 1, d).getDay()]}요일`;
};

const memoColors = [
    { name: "검정", value: "#000000" },
    { name: "빨강", value: "#d32f2f" },
    { name: "파랑", value: "#1976d2" },
    { name: "초록", value: "#388e3c" },
    { name: "노랑", value: "#f9a825" },
];

const convModalStyle: any = {
    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
    width: 920, height: 720, bgcolor: '#fff', borderRadius: '12px', boxShadow: 24,
    outline: 'none', display: 'flex', flexDirection: 'column', overflow: 'hidden'
};
const formModalStyle: any = {
    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
    width: 400, bgcolor: '#fff', borderRadius: '12px', boxShadow: 24, p: 3, outline: 'none'
};
const memoModalStyle: any = {
    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
    width: 1080, maxWidth: '94vw', maxHeight: '92vh', bgcolor: '#fff', borderRadius: '12px', boxShadow: 24, p: 3, outline: 'none',
    display: 'flex', flexDirection: 'column'
};

const Sms: React.FC<any> = (props) => {

    const { user, activateMenuList, unreadSms } = props;

    //면접 스케줄 모달 (문자방에서 열면 이름·지점·근무 미리 채움)
    const [interviewOpen, setInterviewOpen] = useState(false);
    const [interviewPrefill, setInterviewPrefill] = useState<InterviewPrefill | null>(null);
    //공고 관리 모달
    const [jobOpeningOpen, setJobOpeningOpen] = useState(false);

    const [currentMenu, setCurrentMenu] = useState<"inbox" | "bulk" | "contacts">("inbox");
    const [roomSearch, setRoomSearch] = useState("");
    const [roomFilter, setRoomFilter] = useState<"all" | "unreplied">("all");

    const [rooms, setRooms] = useState<any[]>([]);
    const [selectedPhone, setSelectedPhone] = useState<string | null>(null);
    const [selectedName, setSelectedName] = useState("");
    const [messages, setMessages] = useState<any[]>([]);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);

    //연락처
    const [contacts, setContacts] = useState<any[]>([]);
    const [contactForm, setContactForm] = useState<any>({ open: false, mode: "add", id: null, phone: "", name: "", description: "" });

    //공유 메모
    const [memoOpen, setMemoOpen] = useState(false);
    const [memoContent, setMemoContent] = useState("");
    const [memoMeta, setMemoMeta] = useState<any>({ updatedByName: null, updatedAt: null });

    //우클릭 컨텍스트 메뉴
    const [anchorEl, setAnchorEl] = useState<any>(null);
    const [contextPhone, setContextPhone] = useState<string | null>(null);

    //이미지 모달
    const [imageOpen, setImageOpen] = useState(false);
    const [imageSrc, setImageSrc] = useState<string[]>([]);

    const chatBodyRef = useRef<any>(null);
    const stickBottomRef = useRef(true);          //하단 고정 여부(과거 읽는 중이면 강제 스크롤 안 함)
    const convFetchingRef = useRef(false);        //대화 폴링 중복 방지
    const selectedPhoneRef = useRef<string | null>(null);
    const sendingRef = useRef(false);             //발송 동기 재진입 차단(엔터 keydown 중복 방지)
    const tempIdCounterRef = useRef(0);           //낙관적 행 id 충돌 방지
    const memoEditorRef = useRef<any>(null);
    const memoSaveTimerRef = useRef<any>(null);

    useEffect(() => {
        if (activateMenuList) activateMenuList("sms");
    }, [user]);

    useEffect(() => {
        selectedPhoneRef.current = selectedPhone;
    }, [selectedPhone]);

    //방 목록
    const fetchRooms = useCallback(async () => {
        try {
            const result: any = await getFetch("/dashboard/sms/rooms");
            if (result.message === "success") {
                setRooms(result.data);
            }
        } catch (e) {
            //실패 시 기존 목록 유지
        }
    }, []);

    //연락처 목록
    const fetchContacts = useCallback(async () => {
        try {
            const result: any = await getFetch("/dashboard/sms/contacts");
            if (result.message === "success") {
                setContacts(result.data);
            }
        } catch (e) {
            //무시
        }
    }, []);

    //대화 (낙관적 발송 행은 send_mseq로 매칭해 서버 행으로 수렴)
    const fetchConversation = useCallback(async (phone: string) => {
        if (!phone) return;
        if (convFetchingRef.current) return;
        convFetchingRef.current = true;
        try {
            const result: any = await getFetch("/dashboard/sms/conversation?phone=" + encodeURIComponent(phone));
            if (selectedPhoneRef.current !== phone) return;   //그 사이 방 바뀌면 폐기
            if (result.message === "success") {
                const serverRows = result.data;
                setMessages((prev: any[]) => {
                    const optimistic = prev.filter((m: any) => m.optimistic);
                    const serverMseqs = new Set(serverRows.filter((r: any) => r.sendMseq != null).map((r: any) => r.sendMseq));
                    const stillPending = optimistic.filter((o: any) => !(o.sendMseq != null && serverMseqs.has(o.sendMseq)));
                    return [...serverRows, ...stillPending];
                });
            }
        } catch (e) {
            //실패 시 기존 대화 유지
        } finally {
            convFetchingRef.current = false;
        }
    }, []);

    //방 목록 3초 폴링
    useEffect(() => {
        fetchRooms();
        const timer = setInterval(fetchRooms, 3000);
        return () => clearInterval(timer);
    }, [fetchRooms]);

    //연락처 탭 진입 시 로드
    useEffect(() => {
        if (currentMenu === "contacts") fetchContacts();
    }, [currentMenu, fetchContacts]);

    //선택된 방(=모달 열림) 대화 3초 폴링
    useEffect(() => {
        if (!selectedPhone) return;
        setMessages([]);
        stickBottomRef.current = true;
        fetchConversation(selectedPhone);
        const timer = setInterval(() => fetchConversation(selectedPhone), 3000);
        return () => clearInterval(timer);
    }, [selectedPhone, fetchConversation]);

    //메시지 갱신 시 하단 고정이면 스크롤
    useEffect(() => {
        if (stickBottomRef.current && chatBodyRef.current) {
            chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
        }
    }, [messages]);

    const handleScroll = () => {
        const el = chatBodyRef.current;
        if (!el) return;
        stickBottomRef.current = (el.scrollHeight - el.scrollTop - el.clientHeight) < 80;
    };

    const handleImageLoad = () => {
        if (stickBottomRef.current && chatBodyRef.current) {
            chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
        }
    };

    const openConversation = (phone: string, name?: string) => {
        setSelectedName(name || "");
        setSelectedPhone(phone);
    };

    const closeConversation = () => {
        setSelectedPhone(null);
        setSelectedName("");
        fetchRooms();
        if (unreadSms) unreadSms();
    };

    //실제 발송 (입력창 전송 / 재전송 공용)
    const doSend = async (bodyText: string) => {
        const trimmed = (bodyText || "").trim();
        if (!trimmed || !selectedPhone) return;
        if (sendingRef.current) return;   //이미 발송 처리 중이면(엔터 중복 등) 무시
        sendingRef.current = true;

        setSending(true);
        const phone = selectedPhone;      //비동기 콜백용으로 고정
        const tempId = "tmp-" + (++tempIdCounterRef.current);
        const now = new Date();
        const pad = (n: number) => String(n).padStart(2, "0");
        const nowStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
        const optimisticRow = {
            id: tempId,
            direction: "out",
            body: trimmed,
            hasImage: false,
            images: [],
            status: "sending",
            msgAt: nowStr,
            sendMseq: null,
            senderName: (user && user.name) || null,
            optimistic: true
        };
        stickBottomRef.current = true;
        setMessages((prev: any[]) => [...prev, optimisticRow]);

        try {
            const result: any = await postFetch("/dashboard/sms/send", { phone, text: trimmed });
            if (result.message === "success") {
                const sendMseq = result.data.sendMseq;
                setMessages((prev: any[]) => prev.map((m: any) => m.id === tempId ? { ...m, sendMseq } : m));
                fetchConversation(phone);
                fetchRooms();
                if (unreadSms) unreadSms();
            } else {
                setMessages((prev: any[]) => prev.map((m: any) => m.id === tempId ? { ...m, status: "failed:send" } : m));
            }
        } catch (e) {
            setMessages((prev: any[]) => prev.map((m: any) => m.id === tempId ? { ...m, status: "failed:send" } : m));
        } finally {
            sendingRef.current = false;
            setSending(false);
        }
    };

    const handleSend = () => {
        const trimmed = text.trim();
        if (!trimmed) return;
        setText("");
        doSend(trimmed);
    };

    //실패한 발송 재전송 (새 발송 행을 INSERT). 확인창 없이 바로.
    const handleResend = (msg: any) => {
        if (sendingRef.current) return;
        if (!msg || !msg.body) return;
        doSend(msg.body);
    };

    //실패한 발송 삭제. 확인창 없이 바로. 낙관적 행은 상태에서만, 실제 DB 행은 백엔드 삭제.
    const handleDelete = async (msg: any) => {
        if (!msg) return;
        setMessages((prev: any[]) => prev.filter((m: any) => m.id !== msg.id));
        if (msg.optimistic) return;   //로컬 전용 실패행이면 끝
        try {
            await postFetch("/dashboard/sms/delete", { id: msg.id });
            fetchRooms();
        } catch (e) {
            //삭제 실패 시 다음 폴링에서 다시 표시됨
        }
    };

    const handleKeyDown = (e: any) => {
        //한글 IME 조합 중에는 Enter keydown이 두 번 들어옴 → 중복 발송 방지
        if (e.nativeEvent && e.nativeEvent.isComposing) return;
        if (e.keyCode === 229) return;
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    //우클릭 → "읽음 처리하기" (방 열람만으로는 읽음처리 안 함)
    const handleRightClick = (e: any, phone: string) => {
        if (!user || (user.value !== "teacher" && user.value !== "staff")) return;
        if (!user.todoistApiKey) return;
        e.preventDefault();
        e.stopPropagation();
        setContextPhone(phone);
        setAnchorEl(e.currentTarget);
    };

    const handleRead = async () => {
        setAnchorEl(null);
        if (!contextPhone) return;
        if (!window.confirm(`${formatPhone(contextPhone)} 방의 메시지를 읽음 처리 하시겠습니까?`)) return;
        try {
            const result: any = await postFetch("/dashboard/sms/read", { phone: contextPhone });
            if (result.message === "success") {
                fetchRooms();
                if (unreadSms) unreadSms();
            }
        } catch (e) {
            //무시
        }
    };

    const openImage = (images: string[]) => {
        setImageSrc(images);
        setImageOpen(true);
    };

    //연락처 폼
    const openContactForm = (mode: string, data: any) => {
        setContactForm({ open: true, mode, id: data.id || null, phone: data.phone || "", name: data.name || "", description: data.description || "" });
    };
    const closeContactForm = () => {
        setContactForm({ open: false, mode: "add", id: null, phone: "", name: "", description: "" });
    };
    const submitContactForm = async () => {
        const phone = (contactForm.phone || "").replace(/[^0-9]/g, "");   //'-' 포함/미포함 무관하게 숫자만
        const name = (contactForm.name || "").trim();
        const description = (contactForm.description || "").trim();
        if (!name) { alert("이름을 입력하세요."); return; }
        if (!phone) { alert("전화번호를 입력하세요."); return; }
        try {
            const result: any = await postFetch("/dashboard/sms/contacts/save", { phone, name, description });
            if (result.message === "success") {
                const mode = contactForm.mode;
                closeContactForm();
                fetchContacts();
                fetchRooms();
                if (mode === "newsend") {
                    setCurrentMenu("inbox");
                    openConversation(phone, name);
                }
            }
        } catch (e) {
            //무시
        }
    };

    const deleteContact = async (id: number) => {
        try {
            const result: any = await postFetch("/dashboard/sms/contacts/delete", { id });
            if (result.message === "success") {
                fetchContacts();
                fetchRooms();
            }
        } catch (e) {
            //무시
        }
    };

    const toggleBlock = async (c: any) => {
        try {
            const result: any = await postFetch("/dashboard/sms/contacts/block", { phone: c.phone, blocked: c.blocked ? 0 : 1 });
            if (result.message === "success") {
                fetchContacts();
                fetchRooms();
                if (unreadSms) unreadSms();
            }
        } catch (e) {
            //무시
        }
    };

    //공유 메모
    const openMemo = async () => {
        setMemoOpen(true);
        let content = memoContent;   //실패 시 캐시값 유지
        try {
            const result: any = await getFetch("/dashboard/sms/memo");
            if (result.message === "success") {
                content = result.data.content || "";
                setMemoContent(content);
                setMemoMeta({ updatedByName: result.data.updatedByName, updatedAt: result.data.updatedAt });
            }
        } catch (e) {
            //무시
        }
        //모달 열 때마다 에디터에 항상 주입(탭 이동 후 재오픈 시 내용 동일해도 채워지도록 imperative 처리)
        setTimeout(() => { if (memoEditorRef.current) memoEditorRef.current.innerHTML = content; }, 0);
    };
    const scheduleMemoSave = () => {
        if (memoSaveTimerRef.current) clearTimeout(memoSaveTimerRef.current);
        memoSaveTimerRef.current = setTimeout(async () => {
            if (!memoEditorRef.current) return;
            const html = memoEditorRef.current.innerHTML;
            try {
                await postFetch("/dashboard/sms/memo", { content: html });
            } catch (e) {
                //무시
            }
        }, 1000);
    };
    const memoExec = (cmd: string, value?: string) => {
        document.execCommand(cmd, false, value);
        if (memoEditorRef.current) memoEditorRef.current.focus();
        scheduleMemoSave();
    };
    //즉시 저장(닫기 전 flush). 에디터 내용을 동기로 읽은 뒤 POST → 닫혀서 ref가 사라져도 저장됨.
    const saveMemoNow = () => {
        if (memoSaveTimerRef.current) { clearTimeout(memoSaveTimerRef.current); memoSaveTimerRef.current = null; }
        if (!memoEditorRef.current) return;
        const html = memoEditorRef.current.innerHTML;
        setMemoContent(html);   //캐시 동기화(재오픈 시 즉시 반영)
        postFetch("/dashboard/sms/memo", { content: html }).catch(() => { /* 무시 */ });
    };
    const closeMemo = () => {
        saveMemoNow();          //닫을 때 무조건 저장
        setMemoOpen(false);
    };

    const renderStatus = (status: string) => {
        if (!status) return null;
        if (status === "sending") return <ScheduleIcon sx={{ fontSize: 14, color: "#999999" }} />;
        if (status === "sent") return <DoneIcon sx={{ fontSize: 14, color: "#3d50b0" }} />;
        if (status.indexOf("failed") === 0) return <Tooltip title={status}><ErrorIcon sx={{ fontSize: 14, color: "#ff3b30" }} /></Tooltip>;
        return null;
    };

    const canSend = !!text.trim() && !sending && !!selectedPhone;

    //대화 목록 필터('전체'/'미답장') + 검색(이름·전화번호·설명). 클라이언트 필터.
    const unrepliedCount = rooms.filter((r: any) => r.unread > 0).length;
    const baseRooms = roomFilter === "unreplied" ? rooms.filter((r: any) => r.unread > 0) : rooms;
    const q = roomSearch.trim().toLowerCase();
    const qDigits = q.replace(/[^0-9]/g, "");
    const filteredRooms = !q ? baseRooms : baseRooms.filter((r: any) => {
        const name = (r.name || "").toLowerCase();
        const desc = (r.description || "").toLowerCase();
        if (name.includes(q) || desc.includes(q)) return true;
        if (formatPhone(r.phone).toLowerCase().includes(q)) return true;   //010-2345 처럼 하이픈 포함 입력
        if (qDigits && ("" + (r.phone || "")).includes(qDigits)) return true;   //2345 / 01012345678 처럼 숫자 입력
        return false;
    });

    return (
        <div className={styles.main}>

            <div className={styles.title}>
                <img src="img/off/sms.svg" alt="sms" />
                SMS 메시지
            </div>

            {/* 하위 메뉴 탭 */}
            <div className={styles.searchMenu}>
                <div className={`${styles.searchMenuDiv} ${currentMenu === "inbox" ? styles.active : ""}`} onClick={() => setCurrentMenu("inbox")}>메세지함</div>
                <div className={`${styles.searchMenuDiv} ${currentMenu === "bulk" ? styles.active : ""}`} onClick={() => setCurrentMenu("bulk")}>단체 발송하기</div>
                <div className={`${styles.searchMenuDiv} ${currentMenu === "contacts" ? styles.active : ""}`} onClick={() => setCurrentMenu("contacts")}>연락처 관리하기</div>
            </div>

            {/* 메세지함 */}
            {currentMenu === "inbox" && (
                <div className={styles.inboxWrap}>
                    <div className={styles.roomCard}>
                        <div className={styles.inboxHeader}>
                            <span>대화 목록</span>
                            <div className={styles.headerBtns}>
                                <div className={styles.memoIconBtn} onClick={openMemo}>
                                    <StickyNote2Icon sx={{ fontSize: 18, marginRight: "4px" }} /> 공유 메모
                                </div>
                                <div className={styles.memoIconBtn} onClick={() => { setInterviewPrefill(null); setInterviewOpen(true); }}>
                                    <CalendarMonthIcon sx={{ fontSize: 18, marginRight: "4px" }} /> 면접 스케줄
                                </div>
                                <div className={styles.memoIconBtn} onClick={() => setJobOpeningOpen(true)}>
                                    <CampaignIcon sx={{ fontSize: 18, marginRight: "4px" }} /> 공고 관리
                                </div>
                            </div>
                        </div>
                        <div className={styles.roomSearch}>
                            <SearchIcon sx={{ fontSize: 18, color: "#9b988f" }} />
                            <input className={styles.roomSearchInput} placeholder="이름, 전화번호, 설명 검색" value={roomSearch} onChange={(e) => setRoomSearch(e.target.value)} />
                        </div>
                        <div className={styles.filterRow}>
                            <div className={`${styles.filterPill} ${roomFilter === "all" ? styles.active : ""}`} onClick={() => setRoomFilter("all")}>전체</div>
                            <div className={`${styles.filterPill} ${roomFilter === "unreplied" ? styles.active : ""}`} onClick={() => setRoomFilter("unreplied")}>
                                미답장 <span className={styles.filterPillCount}>{unrepliedCount}</span>
                            </div>
                        </div>
                        <div className={styles.roomList}>
                            {filteredRooms.length === 0 ? (
                                <div className={styles.roomEmpty}>{roomSearch.trim() ? "검색 결과가 없습니다." : (roomFilter === "unreplied" ? "미답장 대화가 없습니다." : "대화가 없습니다.")}</div>
                            ) : filteredRooms.map((room: any) => (
                                <div
                                    key={room.phone}
                                    className={styles.eachRoom}
                                    onClick={() => openConversation(room.phone, room.name)}
                                    onContextMenu={(e) => handleRightClick(e, room.phone)}
                                >
                                    <div className={styles.roomAvatar}>
                                        <img src="img/user-solid.svg" alt="user" />
                                    </div>
                                    <div className={styles.roomBody}>
                                        <div className={styles.roomTop}>
                                            <span className={styles.roomName}>{room.name || formatPhone(room.phone)}</span>
                                            <span className={styles.roomTime}>{formatRoomTime(room.lastMsgAt)}</span>
                                        </div>
                                        {room.description ? <div className={styles.roomDesc}>{room.description}</div> : null}
                                        <div className={styles.roomBottom}>
                                            <span className={styles.roomPreview}>{room.preview}</span>
                                            <span className={`${styles.unreadBadge} ${room.unread > 0 ? "" : styles.hidden}`}>{room.unread}</span>
                                        </div>
                                    </div>
                                    <div
                                        className={styles.editIconBtn}
                                        onClick={(e) => { e.stopPropagation(); openContactForm("editroom", { phone: room.phone, name: room.name || "", description: room.description || "" }); }}
                                    >
                                        <EditIcon sx={{ fontSize: 18 }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={styles.fab} onClick={() => openContactForm("newsend", { phone: "", name: "", description: "" })}>
                        <img src="./img/pencil.svg" alt="new" /> 새로 발송하기
                    </div>
                </div>
            )}

            {/* 단체 발송하기 */}
            {currentMenu === "bulk" && <SmsBulkSend user={user} />}

            {/* 연락처 관리하기 */}
            {currentMenu === "contacts" && (
                <div className={styles.contactsCard}>
                    <div className={styles.contactsHeader}>
                        <span>연락처</span>
                        <div className={styles.contactAddBtn} onClick={() => openContactForm("add", { phone: "", name: "", description: "" })}>+ 연락처 추가</div>
                    </div>
                    <div className={styles.contactList}>
                        <div className={`${styles.contactRow} ${styles.contactHead}`}>
                            <span className={styles.cName}>이름</span>
                            <span className={styles.cPhone}>전화번호</span>
                            <span className={styles.cDesc}>설명</span>
                            <span className={styles.cBlock}>차단</span>
                            <span className={styles.cAct}>관리</span>
                        </div>
                        {contacts.length === 0 ? (
                            <div className={styles.roomEmpty}>저장된 연락처가 없습니다.</div>
                        ) : contacts.map((c: any) => (
                            <div key={c.id} className={styles.contactRow}>
                                <span className={styles.cName}>{c.name || "-"}</span>
                                <span className={styles.cPhone}>{formatPhone(c.phone)}</span>
                                <span className={styles.cDesc}>{c.description || ""}</span>
                                <span className={styles.cBlock}><Switch size="small" checked={!!c.blocked} onChange={() => toggleBlock(c)} /></span>
                                <span className={styles.cAct}>
                                    <span className={styles.linkBtn} onClick={() => openContactForm("edit", { id: c.id, phone: c.phone, name: c.name || "", description: c.description || "" })}>수정</span>
                                    <span className={styles.linkBtnRed} onClick={() => deleteContact(c.id)}>삭제</span>
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* 대화 모달 (zIndex 500: FAB(10) 위, 이미지 뷰어(react-viewer ~1000) 아래로 둬서 사진 클릭 시 뷰어가 위로 뜨게) */}
            <Modal open={!!selectedPhone} onClose={closeConversation} sx={{ zIndex: 500 }}>
                <Box sx={convModalStyle}>
                    <div className={styles.modalHeader}>
                        <span className={styles.modalTitleText}>
                            {selectedName ? selectedName + " · " : ""}{selectedPhone ? formatPhone(selectedPhone) : ""}
                        </span>
                        <div className={styles.headerBtns}>
                            <div className={styles.memoIconBtn} onClick={() => {
                                const room = rooms.find((r: any) => r.phone === selectedPhone);
                                setInterviewPrefill(parseRoomDefaults(selectedName, room?.description || "", selectedPhone || ""));
                                setInterviewOpen(true);
                            }}>
                                <CalendarMonthIcon sx={{ fontSize: 18, marginRight: "4px" }} /> 면접스케쥴
                            </div>
                            <CloseIcon sx={{ cursor: "pointer", color: "#888" }} onClick={closeConversation} />
                        </div>
                    </div>
                    <div className={styles.chatBody} ref={chatBodyRef} onScroll={handleScroll}>
                        {messages.map((msg: any, idx: number) => {
                            //이전 메시지와 날짜가 다르면 구분선 ("메시지" 메뉴 팝업과 동일한 Divider+Chip)
                            const msgDate = ((msg.msgAt || "").split(" ")[0]) || "";
                            const prevDate = idx > 0 ? (((messages[idx - 1].msgAt || "").split(" ")[0]) || "") : "";
                            const showDateDivider = !!msgDate && msgDate !== prevDate;
                            return (
                            <React.Fragment key={msg.id}>
                            {showDateDivider ? (
                                <div className={styles.dateDivider}>
                                    <Divider light={true}>
                                        <Chip label={formatDateDivider(msg.msgAt)} />
                                    </Divider>
                                </div>
                            ) : null}
                            <div className={`${styles.msgRow} ${msg.direction === "out" ? styles.out : styles.in}`}>
                                <div className={styles.msgGroup}>
                                    <div className={styles.bubbleCol}>
                                        {msg.direction === "out" && msg.senderName ? <div className={styles.senderName}>{msg.senderName}</div> : null}
                                        <div className={`${styles.msgItem} ${msg.direction === "out" ? styles.ofOut : ""}`}>
                                            {msg.body ? <div className={styles.msgText}>{msg.body}</div> : null}
                                            {(msg.images || []).map((src: string, i: number) => (
                                                <img key={i} src={src} alt="첨부" className={styles.msgImg} onClick={() => openImage(msg.images)} onLoad={handleImageLoad} />
                                            ))}
                                        </div>
                                    </div>
                                    <div className={styles.msgMeta}>
                                        {msg.direction === "out" ? (
                                            <div className={styles.statusIcon}>
                                                {renderStatus(msg.status)}
                                                {(msg.status && msg.status.indexOf("failed") === 0) ? (
                                                    <>
                                                        <span className={styles.resendBtn} onClick={() => handleResend(msg)}>재전송</span>
                                                        <span className={styles.deleteBtn} onClick={() => handleDelete(msg)}>삭제</span>
                                                    </>
                                                ) : null}
                                            </div>
                                        ) : null}
                                        <span>{formatClock(msg.msgAt)}</span>
                                    </div>
                                </div>
                            </div>
                            </React.Fragment>
                            );
                        })}
                    </div>
                    <div className={styles.sendBox}>
                        <TextareaAutosize
                            className={styles.sendInput}
                            placeholder="메시지를 입력하세요 (Enter 전송, Shift+Enter 줄바꿈)"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            onKeyDown={handleKeyDown}
                            minRows={1}
                            maxRows={4}
                        />
                        <div className={`${styles.sendBtn} ${canSend ? styles.active : ""}`} onClick={handleSend}>
                            {sending ? <CircularProgress size={18} sx={{ color: "#bdbdbd" }} /> : <SendIcon sx={{ fontSize: 20 }} />}
                        </div>
                    </div>
                </Box>
            </Modal>

            {/* 연락처 폼 모달 (추가/수정/새발송 공용) */}
            <Modal open={contactForm.open} onClose={closeContactForm}>
                <Box sx={formModalStyle}>
                    <div className={styles.formTitle}>
                        {contactForm.mode === "newsend" ? "새로 발송하기" : contactForm.mode === "add" ? "연락처 추가" : "연락처 수정"}
                    </div>
                    <TextField label="이름" value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} fullWidth size="small" sx={{ mb: 2 }} />
                    <TextField label="전화번호" value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} fullWidth size="small" sx={{ mb: 2 }} placeholder="01012345678" disabled={contactForm.mode === "edit" || contactForm.mode === "editroom"} />
                    <TextField label="설명 (선택)" value={contactForm.description} onChange={(e) => setContactForm({ ...contactForm, description: e.target.value })} fullWidth size="small" sx={{ mb: 2 }} />
                    <div className={styles.formActions}>
                        <Button onClick={closeContactForm}>취소</Button>
                        <Button variant="contained" onClick={submitContactForm}>{contactForm.mode === "newsend" ? "발송 시작" : "저장"}</Button>
                    </div>
                </Box>
            </Modal>

            {/* 공유 메모 모달 (서식 에디터) */}
            <Modal open={memoOpen} onClose={closeMemo}>
                <Box sx={memoModalStyle}>
                    <div className={styles.modalHeader}>
                        <span className={styles.modalTitleText}>공유 메모</span>
                        <CloseIcon sx={{ cursor: "pointer", color: "#888" }} onClick={closeMemo} />
                    </div>
                    <div className={styles.memoToolbar}>
                        <button className={styles.memoToolBtn} onMouseDown={(e) => e.preventDefault()} onClick={() => memoExec("bold")}><b>B</b></button>
                        {memoColors.map((c) => (
                            <button key={c.value} className={styles.memoColorBtn} style={{ backgroundColor: c.value }} title={c.name} onMouseDown={(e) => e.preventDefault()} onClick={() => memoExec("foreColor", c.value)} />
                        ))}
                    </div>
                    <div className={styles.memoEditor} contentEditable suppressContentEditableWarning ref={memoEditorRef} onInput={scheduleMemoSave}></div>
                    <div className={styles.memoMeta}>
                        {memoMeta.updatedByName ? `마지막 수정: ${memoMeta.updatedByName} ${memoMeta.updatedAt || ""}` : "입력하면 자동 저장됩니다."}
                    </div>
                </Box>
            </Modal>

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                <MenuItem onClick={handleRead}>읽음 처리하기</MenuItem>
            </Menu>

            <InterviewScheduleModal
                open={interviewOpen}
                onClose={() => setInterviewOpen(false)}
                user={user}
                prefill={interviewPrefill}
            />

            <JobOpeningModal open={jobOpeningOpen} onClose={() => setJobOpeningOpen(false)} />

            <ImageModal2
                open={imageOpen}
                handleClose={() => setImageOpen(false)}
                src={imageSrc}
                isMultiple={imageSrc.length > 1}
            />

        </div>
    );
};

export default Sms;
